/**
 * Subdomain Detection Utility for Taealam
 * Detects current subdomain and provides URL building helpers
 */

/**
 * Get the current subdomain from the browser URL
 * @returns {string} - 'teacher', 'main', or detected subdomain
 */
export const getSubdomain = () => {
  const hostname = window.location.hostname;

  // Local development with teacher subdomain
  if (hostname === 'teacher.localhost') {
    return 'teacher';
  }

  // Skip Vercel deployment URLs (they have multiple dots but aren't subdomains)
  // Handle both: taealam-homepage.vercel.app and taealam-homepage-xxx.vercel.app
  if (hostname.includes('.vercel.app') || hostname.includes('.vercel-dns.com') || hostname.endsWith('.vercel.app')) {
    return 'main';
  }

  // Production subdomain detection (only for actual custom domains)
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    const subdomain = parts[0].toLowerCase(); // Case-insensitive matching
    // Only treat as teacher subdomain if it's explicitly 'teacher'
    if (subdomain === 'teacher') {
      return 'teacher';
    }
    // For custom domains, treat www and other subdomains as main
    // (unless it's explicitly 'teacher')
    return 'main';
  }

  // No subdomain (main domain or localhost)
  return 'main';
};

/**
 * Check if we're currently on the teacher subdomain
 * @returns {boolean}
 */
export const isTeacherSubdomain = () => {
  return getSubdomain() === 'teacher';
};

/**
 * Check if we're currently on the main/student subdomain
 * @returns {boolean}
 */
export const isMainSubdomain = () => {
  const subdomain = getSubdomain();
  return subdomain === 'main' || subdomain === 'www';
};

/**
 * Get the appropriate cross-domain URL for a given role
 * @param {string} targetRole - 'teacher' or 'student'
 * @param {string} path - Path to navigate to (default: '/')
 * @returns {string} - Complete URL for cross-domain navigation
 */
export const getCrossDomainUrl = (targetRole, path = '/') => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;

  // Development environment (localhost)
  if (hostname.includes('localhost')) {
    const base = targetRole === 'teacher' ? 'teacher.localhost' : 'localhost';
    return `${protocol}//${base}${port ? ':' + port : ''}${path}`;
  }

  // Production environment
  // Extract base domain (e.g., 'taealam.bh' from 'teacher.taealam.bh')
  const baseDomain = hostname.split('.').slice(-2).join('.');
  const subdomain = targetRole === 'teacher' ? 'teacher.' : '';
  return `${protocol}//${subdomain}${baseDomain}${path}`;
};

/**
 * Navigate to a different subdomain
 * @param {string} targetRole - 'teacher' or 'student'
 * @param {string} path - Path to navigate to
 */
export const navigateToCrossDomain = (targetRole, path = '/') => {
  window.location.href = getCrossDomainUrl(targetRole, path);
};

/**
 * Check if a route belongs to the teacher subdomain
 * @param {string} pathname - The route path to check
 * @returns {boolean}
 */
export const isTeacherRoute = (pathname) => {
  const teacherRoutes = [
    '/dashboard/teacher',
    '/become-teacher',
    '/become-teacher-ar',
    '/teacher-confirm'
  ];
  return teacherRoutes.some(route => pathname.startsWith(route));
};

/**
 * Check if a route belongs to the student/main subdomain
 * @param {string} pathname - The route path to check
 * @returns {boolean}
 */
export const isStudentRoute = (pathname) => {
  const studentRoutes = [
    '/dashboard/student',
    '/find-teacher',
    '/find-teacher-ar',
    '/about',
    '/how-it-works',
    '/contact-us',
    '/institute',
    '/leaderboard'
  ];
  return studentRoutes.some(route => pathname.startsWith(route));
};

/**
 * Get the appropriate subdomain for a user role
 * @param {string} role - User role ('student', 'teacher', 'admin', 'institute')
 * @returns {string} - 'teacher' or 'student'
 */
export const getSubdomainForRole = (role) => {
  return role === 'teacher' ? 'teacher' : 'student';
};

export default {
  getSubdomain,
  isTeacherSubdomain,
  isMainSubdomain,
  getCrossDomainUrl,
  navigateToCrossDomain,
  isTeacherRoute,
  isStudentRoute,
  getSubdomainForRole
};
